import { useEffect, useRef, useState } from "react";
import * as THREE from "three";

/**
 * A handcrafted Three.js earth globe with episode location pins.
 * - Wireframe glow earth (no external textures, no AI-slop blue marble)
 * - Glowing gold pins per episode
 * - Hover → callout with episode info
 * - Click → triggers onSelect callback
 */
export const Globe3D = ({ episodes = [], futurePins = [], onSelect }) => {
  const mountRef = useRef(null);
  const tooltipRef = useRef(null);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const width = mount.clientWidth;
    const height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.z = 3.4;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);

    const globeGroup = new THREE.Group();
    scene.add(globeGroup);

    // ── Realistic earth: blue marble texture (NASA-derived, CORS-friendly)
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin("anonymous");

    const baseGeo = new THREE.SphereGeometry(1, 96, 96);
    const baseMat = new THREE.MeshPhongMaterial({
      color: 0xffffff,
      specular: 0x222233,
      shininess: 8,
    });
    // Day texture (continents, oceans)
    loader.load(
      "https://unpkg.com/three-globe@2.31.1/example/img/earth-blue-marble.jpg",
      (t) => { baseMat.map = t; baseMat.needsUpdate = true; }
    );
    // Bump/topology for terrain feel
    loader.load(
      "https://unpkg.com/three-globe@2.31.1/example/img/earth-topology.png",
      (t) => { baseMat.bumpMap = t; baseMat.bumpScale = 0.03; baseMat.needsUpdate = true; }
    );
    const baseSphere = new THREE.Mesh(baseGeo, baseMat);
    globeGroup.add(baseSphere);

    // Lights for the realistic earth
    const sun = new THREE.DirectionalLight(0xfff1d0, 1.6);
    sun.position.set(5, 3, 5);
    scene.add(sun);
    const ambient = new THREE.AmbientLight(0x404060, 0.55);
    scene.add(ambient);
    const fillRim = new THREE.DirectionalLight(0xC9A04A, 0.45);
    fillRim.position.set(-3, -1, -2);
    scene.add(fillRim);

    // ── Subtle gold graticule lines on top of the texture
    const wireGeo = new THREE.SphereGeometry(1.002, 36, 18);
    const wireMat = new THREE.MeshBasicMaterial({
      color: 0xC9A04A,
      wireframe: true,
      transparent: true,
      opacity: 0.08,
    });
    const wireSphere = new THREE.Mesh(wireGeo, wireMat);
    globeGroup.add(wireSphere);

    // ── Atmosphere haze
    const atmoGeo = new THREE.SphereGeometry(1.04, 64, 64);
    const atmoMat = new THREE.ShaderMaterial({
      transparent: true,
      side: THREE.BackSide,
      uniforms: { c: { value: new THREE.Color(0x6ab6e8) } },
      vertexShader: `
        varying float intensity;
        void main() {
          vec3 vNormal = normalize(normalMatrix * normal);
          intensity = pow(0.7 - dot(vNormal, vec3(0.0,0.0,1.0)), 2.0);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: `
        uniform vec3 c;
        varying float intensity;
        void main() {
          gl_FragColor = vec4(c, intensity * 0.55);
        }`,
    });
    const atmo = new THREE.Mesh(atmoGeo, atmoMat);
    globeGroup.add(atmo);

    // Outer warm glow halo
    const glowGeo = new THREE.SphereGeometry(1.22, 64, 64);
    const glowMat = new THREE.ShaderMaterial({
      transparent: true,
      side: THREE.BackSide,
      uniforms: {
        c: { value: new THREE.Color(0xC9A04A) },
      },
      vertexShader: `
        varying float intensity;
        void main() {
          vec3 vNormal = normalize(normalMatrix * normal);
          intensity = pow(0.62 - dot(vNormal, vec3(0.0,0.0,1.0)), 2.6);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 c;
        varying float intensity;
        void main() {
          vec3 glow = c * intensity;
          gl_FragColor = vec4(glow, intensity * 0.95);
        }
      `,
    });
    const glow = new THREE.Mesh(glowGeo, glowMat);
    globeGroup.add(glow);

    // Sprinkle of dot continents (subtle, decorative)
    const dotsGeo = new THREE.BufferGeometry();
    const positions = [];
    for (let i = 0; i < 1200; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 1.005;
      positions.push(
        r * Math.sin(phi) * Math.cos(theta),
        r * Math.cos(phi),
        r * Math.sin(phi) * Math.sin(theta),
      );
    }
    dotsGeo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    const dotsMat = new THREE.PointsMaterial({
      color: 0xE8C56A,
      size: 0.012,
      transparent: true,
      opacity: 0.55,
    });
    const dots = new THREE.Points(dotsGeo, dotsMat);
    globeGroup.add(dots);

    // Convert lat/lng to xyz on unit sphere
    const latLngToVec3 = (lat, lng, radius = 1.02) => {
      const phi = (90 - lat) * (Math.PI / 180);
      const theta = (lng + 180) * (Math.PI / 180);
      return new THREE.Vector3(
        -radius * Math.sin(phi) * Math.cos(theta),
        radius * Math.cos(phi),
        radius * Math.sin(phi) * Math.sin(theta),
      );
    };

    // Pins
    const pinsGroup = new THREE.Group();
    globeGroup.add(pinsGroup);

    const pinMeshes = [];
    episodes.forEach((ep) => {
      const pos = latLngToVec3(ep.lat, ep.lng, 1.02);

      // Taller golden pin needle
      const needleGeo = new THREE.CylinderGeometry(0.004, 0.004, 0.16, 8);
      const needleMat = new THREE.MeshBasicMaterial({ color: 0xC9A04A });
      const needle = new THREE.Mesh(needleGeo, needleMat);
      needle.position.copy(pos.clone().multiplyScalar(1.08));
      needle.lookAt(0, 0, 0);
      needle.rotateX(Math.PI / 2);
      pinsGroup.add(needle);

      // Large bright pin head (click target) — INVISIBLE bigger hit-sphere + visible smaller sphere
      const tipGeo = new THREE.SphereGeometry(0.038, 20, 20);
      const tipMat = new THREE.MeshBasicMaterial({ color: 0xFFE08A });
      const tip = new THREE.Mesh(tipGeo, tipMat);
      tip.position.copy(pos.clone().multiplyScalar(1.17));
      tip.userData.episode = ep;
      pinsGroup.add(tip);
      pinMeshes.push(tip);

      // Halo behind the pin head
      const haloGeo = new THREE.SphereGeometry(0.055, 16, 16);
      const haloMat = new THREE.MeshBasicMaterial({
        color: 0xE8C56A, transparent: true, opacity: 0.35,
      });
      const halo = new THREE.Mesh(haloGeo, haloMat);
      halo.position.copy(pos.clone().multiplyScalar(1.17));
      pinsGroup.add(halo);

      // Glowing ring on globe surface
      const ringGeo = new THREE.RingGeometry(0.05, 0.075, 32);
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0xC9A04A, side: THREE.DoubleSide, transparent: true, opacity: 0.7,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.copy(pos.clone().multiplyScalar(1.005));
      ring.lookAt(pos.clone().multiplyScalar(2));
      pinsGroup.add(ring);

      // Light beam shooting up from pin
      const beamGeo = new THREE.CylinderGeometry(0.006, 0.001, 0.3, 8, 1, true);
      const beamMat = new THREE.MeshBasicMaterial({
        color: 0xFFE08A, transparent: true, opacity: 0.5, side: THREE.DoubleSide,
      });
      const beam = new THREE.Mesh(beamGeo, beamMat);
      beam.position.copy(pos.clone().multiplyScalar(1.22));
      beam.lookAt(0, 0, 0);
      beam.rotateX(Math.PI / 2);
      pinsGroup.add(beam);
    });

    // Season 2 — holographic blue pins (no click, just glowing markers)
    futurePins.forEach((p) => {
      const pos = latLngToVec3(p.lat, p.lng, 1.02);

      // Vertical needle (blue)
      const nGeo = new THREE.CylinderGeometry(0.003, 0.003, 0.1, 6);
      const nMat = new THREE.MeshBasicMaterial({ color: 0x6AB6E8 });
      const n = new THREE.Mesh(nGeo, nMat);
      n.position.copy(pos.clone().multiplyScalar(1.05));
      n.lookAt(0, 0, 0);
      n.rotateX(Math.PI / 2);
      pinsGroup.add(n);

      // Pulsing blue tip
      const tGeo = new THREE.SphereGeometry(0.025, 16, 16);
      const tMat = new THREE.MeshBasicMaterial({ color: 0x9ED4F2, transparent: true, opacity: 0.85 });
      const t = new THREE.Mesh(tGeo, tMat);
      t.position.copy(pos.clone().multiplyScalar(1.11));
      t.userData.isS2 = true;
      pinsGroup.add(t);

      // Halo ring (blue)
      const rGeo = new THREE.RingGeometry(0.035, 0.05, 24);
      const rMat = new THREE.MeshBasicMaterial({
        color: 0x6AB6E8, side: THREE.DoubleSide, transparent: true, opacity: 0.4,
      });
      const r = new THREE.Mesh(rGeo, rMat);
      r.position.copy(pos.clone().multiplyScalar(1.04));
      r.lookAt(0, 0, 0);
      pinsGroup.add(r);
    });

    // Raycaster for hover/click
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    let isDragging = false;
    let dragStart = { x: 0, y: 0 };
    // Initial rotation: zoom in on India
    let rotation = { x: 0.25, y: 3.1 };
    let velocity = { x: 0, y: 0 };
    let autoRotate = true;

    const onMouseMove = (e) => {
      const rect = mount.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / height) * 2 + 1;

      if (isDragging) {
        velocity.y = (e.clientX - dragStart.x) * 0.005;
        velocity.x = (e.clientY - dragStart.y) * 0.003;
        rotation.y += velocity.y;
        rotation.x = Math.max(-0.8, Math.min(0.8, rotation.x + velocity.x));
        dragStart = { x: e.clientX, y: e.clientY };
        autoRotate = false;
      }

      // Hit test
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(pinMeshes);
      if (hits.length > 0) {
        const ep = hits[0].object.userData.episode;
        if (tooltipRef.current) {
          tooltipRef.current.style.left = `${e.clientX - rect.left}px`;
          tooltipRef.current.style.top = `${e.clientY - rect.top}px`;
        }
        setHovered(ep);
        mount.style.cursor = "pointer";
      } else {
        setHovered(null);
        mount.style.cursor = isDragging ? "grabbing" : "grab";
      }
    };

    const onMouseDown = (e) => {
      isDragging = true;
      dragStart = { x: e.clientX, y: e.clientY };
      mount.style.cursor = "grabbing";
    };
    const onMouseUp = () => {
      isDragging = false;
      mount.style.cursor = "grab";
      setTimeout(() => { autoRotate = true; }, 2200);
    };
    const onClick = (e) => {
      const rect = mount.getBoundingClientRect();
      mouse.x = ((e.clientX - rect.left) / width) * 2 - 1;
      mouse.y = -((e.clientY - rect.top) / height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(pinMeshes);
      if (hits.length > 0 && onSelect) {
        onSelect(hits[0].object.userData.episode);
      }
    };

    mount.addEventListener("mousemove", onMouseMove);
    mount.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mouseup", onMouseUp);
    mount.addEventListener("click", onClick);

    const onResize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    let frame;
    const animate = (t) => {
      if (autoRotate) rotation.y += 0.0015;
      globeGroup.rotation.x = rotation.x;
      globeGroup.rotation.y = rotation.y;
      // Pulse the pin tips
      pinMeshes.forEach((p, i) => {
        const scale = 1 + Math.sin(t * 0.003 + i) * 0.18;
        p.scale.setScalar(scale);
      });
      renderer.render(scene, camera);
      frame = requestAnimationFrame(animate);
    };
    animate(0);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mouseup", onMouseUp);
      mount.removeEventListener("mousemove", onMouseMove);
      mount.removeEventListener("mousedown", onMouseDown);
      mount.removeEventListener("click", onClick);
      mount.removeChild(renderer.domElement);
      renderer.dispose();
      baseGeo.dispose(); baseMat.dispose();
      wireGeo.dispose(); wireMat.dispose();
      atmoGeo.dispose(); atmoMat.dispose();
      glowGeo.dispose(); glowMat.dispose();
    };
  }, [episodes, futurePins, onSelect]);

  return (
    <div className="relative w-full" style={{ aspectRatio: "1 / 1", maxWidth: 680 }}>
      <div
        ref={mountRef}
        data-testid="globe-canvas"
        className="w-full h-full st-interactive"
        style={{ cursor: "grab" }}
      />
      <div
        ref={tooltipRef}
        data-testid="globe-tooltip"
        className="absolute pointer-events-none -translate-x-1/2 -translate-y-[120%]"
        style={{
          opacity: hovered ? 1 : 0,
          transition: "opacity .2s",
          background: "rgba(8,6,4,.92)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(201,160,74,.4)",
          padding: "14px 18px",
          minWidth: 220,
          zIndex: 20,
        }}
      >
        {hovered && (
          <>
            <div className="st-cinzel text-[0.52rem] tracking-[0.4em] uppercase text-[var(--gold)] mb-1">
              Episode {String(hovered.number).padStart(2, "0")}
            </div>
            <div className="st-cormorant text-base font-semibold text-[var(--cream)]">{hovered.guest}</div>
            <div className="text-[0.78rem] text-[var(--fog)]">{hovered.location}</div>
            <div className="mt-2 st-cinzel text-[0.5rem] tracking-[0.3em] uppercase text-[var(--gold)]">Click to watch ↗</div>
          </>
        )}
      </div>
    </div>
  );
};
